import { useContext, useCallback, useRef } from 'react';
import { withErrorBoundary } from '~/components/Tools';
import { LocalAuthContext } from '~/components/Auth';
import { useFreshchat } from '~/components/Freshchat';
import { useBerry } from '@mindtickle/ui-shell-core/Berry';
import { singletonTTIPromises, DELAYS } from '@mindtickle/ui-shell-core/tti';
import { useEchoWidget } from '~/components/Echo';
import { initWhatfix } from '~/components/Whatfix';
import { DarkColorsPalette } from '@mindtickle/styles/lib';
import { isAdminSite } from '~/utils';
import canLoadFreshchat from '../helpers/freshchatHelpers';
import { useUserAuth } from '~/components/Auth';
import { BERRY_WIDGET_ID } from '~/config/app.constants';
import { BERRY_TOGGLE_ICON_DATA_URI } from '../config/constants';

function DeferredIntegrations() {
  const isAdmin = !!isAdminSite();
  const { tempFeatureConfig = {} } = useUserAuth();

  const auth = useContext(LocalAuthContext);
  const integrationsAlreadyInitialized = useRef(false);
  const isBerryChatDisabled = auth?.featureToggles.isBerryChatDisabled;

  const {
    init: initFreshchat,
    onAdminBerryHandOff,
    onLearnerBerryHandOff,
    onBerryHandShake,
  } = useFreshchat(auth, {
    isBerryChatDisabled,
  });

  const { init: initEcho } = useEchoWidget(auth);

  const { init: initBerry } = useBerry(
    {
      ...auth,
      isBerryChatEnabled: !isBerryChatDisabled,
      primaryColor: DarkColorsPalette.COLOR_BRAND_PRIMARY,
      customerId: tempFeatureConfig?.berryCannedBotId || auth?.company?.id,
      berryBotUrlPath: tempFeatureConfig?.berryBotUrlPath,
      toggleIconUrl: BERRY_TOGGLE_ICON_DATA_URI,
      parentElementId: BERRY_WIDGET_ID,
    },
    { onBerryHandOff: isAdmin ? onAdminBerryHandOff : onLearnerBerryHandOff, onBerryHandShake }
  );

  const init = useCallback(async () => {
    await singletonTTIPromises[DELAYS.L];

    // initializing whatfix
    initWhatfix(auth);

    if (!isBerryChatDisabled) {
      initBerry();
    }

    initEcho();

    const isFreshchatEnabled = await canLoadFreshchat({ isAdmin, auth });
    if (isFreshchatEnabled) {
      initFreshchat();
    }
  }, [auth, isAdmin, isBerryChatDisabled, initFreshchat, initBerry]);

  if (!integrationsAlreadyInitialized.current) {
    init();
    integrationsAlreadyInitialized.current = true;
  }

  return null;
}

export default withErrorBoundary(DeferredIntegrations, {});
